<template>
    <v-container>
        <v-row>
            <v-col cols="12">
                <h3 class="section-title">Períodos de Indisponibilidade</h3>
                <p class="section-desc">Registre os intervalos em que este hotel não estará disponível para novas reservas.</p>
            </v-col>

            <v-col cols="12">
                <v-data-table
                    :headers="headers"
                    :items="periods"
                    :loading="loading"
                    :items-per-page="10"
                    no-data-text="Nenhum período cadastrado."
                >
                    <template v-slot:item.start_date="{ item }">
                        {{ formatDate(item.start_date) }}
                    </template>
                    <template v-slot:item.end_date="{ item }">
                        {{ formatDate(item.end_date) }}
                    </template>
                    <template v-slot:item.total_days="{ item }">
                        {{ calculateTotalDays(item.start_date, item.end_date) }}
                    </template>
                    <template v-slot:item.status="{ item }">
                        <v-chip x-small :color="periodStatus(item).color" :dark="periodStatus(item).dark">
                            {{ periodStatus(item).label }}
                        </v-chip>
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <v-btn icon small @click="openDeleteDialog(item)">
                            <v-icon color="error">mdi-delete-outline</v-icon>
                        </v-btn>
                    </template>
                </v-data-table>
            </v-col>

            <v-col cols="12">
                <v-card outlined class="mt-2 pa-5">
                    <p class="entry-label">Adicione um novo período de indisponibilidade:</p>

                    <v-form ref="addForm" v-model="addFormValid">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-menu
                                    v-model="startDateMenu"
                                    :close-on-content-click="false"
                                    max-width="290"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="newStartDate"
                                            label="Data de início"
                                            outlined
                                            readonly
                                            :rules="[v => !!v || 'Campo obrigatório']"
                                            append-icon="mdi-calendar"
                                            v-bind="attrs"
                                            v-on="on"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="newStartDate"
                                        no-title
                                        locale="pt-BR"
                                        :first-day-of-week="0"
                                        @input="startDateMenu = false"
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-menu
                                    v-model="endDateMenu"
                                    :close-on-content-click="false"
                                    max-width="290"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="newEndDate"
                                            label="Data de fim"
                                            outlined
                                            readonly
                                            :rules="endDateRules"
                                            append-icon="mdi-calendar"
                                            v-bind="attrs"
                                            v-on="on"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="newEndDate"
                                        no-title
                                        locale="pt-BR"
                                        :first-day-of-week="0"
                                        :min="newStartDate"
                                        @input="endDateMenu = false"
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>
                        </v-row>

                        <v-row class="mt-0">
                            <v-col cols="12">
                                <v-btn
                                    color="primary"
                                    :loading="addFormLoading"
                                    class="mr-3"
                                    @click="createPeriod"
                                >
                                    Salvar
                                </v-btn>
                                <v-btn outlined @click="clearForm">
                                    Limpar
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card>
            </v-col>

            <confirm-deletion-modal
                :dialog-active="deleteDialog.active"
                :dialog-loading="deleteDialog.loading"
                :entity-to-delete="deleteDialog.label"
                @delete-method="confirmDelete"
                @close-dialog="deleteDialog.active = false"
            />

            <unavailability-conflict-modal
                :active="conflictModal.active"
                :loading="conflictModal.loading"
                :pending-bookings="conflictModal.pendingBookings"
                :confirmed-bookings="conflictModal.confirmedBookings"
                @confirm="confirmConflictAndCreate"
                @cancel="conflictModal.active = false"
            />
        </v-row>
    </v-container>
</template>

<script>
import hotelUnavailabilityPeriodService from '../../../services/hotelUnavailabilityPeriodService';
import ConfirmDeletionModal from '../../Modals/ConfirmDeletionModal.vue';
import UnavailabilityConflictModal from './UnavailabilityConflictModal.vue';

export default {
    name: 'UnavailabilityPeriods',

    components: { ConfirmDeletionModal, UnavailabilityConflictModal },

    props: {
        hotelId: {
            type: Number,
            required: true,
        },
    },

    data: () => ({
        periods: [],
        loading: false,
        addFormValid: false,
        addFormLoading: false,
        newStartDate: null,
        newEndDate: null,
        startDateMenu: false,
        endDateMenu: false,
        deleteDialog: {
            active: false,
            loading: false,
            label: '',
            periodId: null,
        },
        conflictModal: {
            active: false,
            loading: false,
            pendingBookings: [],
            confirmedBookings: [],
        },
        headers: [
            { text: 'Data de início', value: 'start_date',  sortable: false },
            { text: 'Data de fim',    value: 'end_date',    sortable: false },
            { text: 'Total de dias',  value: 'total_days',  sortable: false },
            { text: 'Status',         value: 'status',      sortable: false },
            { text: '',               value: 'actions',     sortable: false, align: 'end' },
        ],
    }),

    computed: {
        endDateRules() {
            return [
                v => !!v || 'Campo obrigatório',
                v => !this.newStartDate || v >= this.newStartDate || 'A data de fim deve ser igual ou posterior à data de início',
            ];
        },
    },

    mounted() {
        this.loadPeriods();
    },

    methods: {
        async loadPeriods() {
            try {
                this.loading = true;
                const res = await hotelUnavailabilityPeriodService.list(this.hotelId);
                this.periods = res.data.data;
            } catch (e) {
                this.$simpleDialog('Erro ao carregar os períodos de indisponibilidade.', 'error');
            } finally {
                this.loading = false;
            }
        },

        clearForm() {
            this.newStartDate = null;
            this.newEndDate = null;
            this.$refs.addForm.reset();
        },

        async createPeriod() {
            if (!this.$refs.addForm.validate()) return;
            try {
                this.addFormLoading = true;
                const conflictRes = await hotelUnavailabilityPeriodService.checkConflicts(
                    this.hotelId,
                    this.newStartDate,
                    this.newEndDate
                );
                const pending   = conflictRes.data.data.pending   || [];
                const confirmed = conflictRes.data.data.confirmed || [];

                if (pending.length > 0 || confirmed.length > 0) {
                    this.conflictModal.pendingBookings   = pending;
                    this.conflictModal.confirmedBookings = confirmed;
                    this.conflictModal.active = true;
                    return;
                }

                await this.doCreatePeriod();
            } catch (e) {
                this.$simpleDialog('Erro ao verificar conflitos. Tente novamente.', 'error');
            } finally {
                this.addFormLoading = false;
            }
        },

        async confirmConflictAndCreate() {
            try {
                this.conflictModal.loading = true;
                await this.doCreatePeriod();
                this.conflictModal.active = false;
            } catch (e) {
                this.$simpleDialog('Erro ao criar o período de indisponibilidade.', 'error');
            } finally {
                this.conflictModal.loading = false;
            }
        },

        async doCreatePeriod() {
            await hotelUnavailabilityPeriodService.create(this.hotelId, {
                start_date: this.newStartDate,
                end_date:   this.newEndDate,
            });
            this.clearForm();
            await this.loadPeriods();
            this.$simpleDialog('Período de indisponibilidade criado com sucesso.', 'success');
        },

        openDeleteDialog(period) {
            this.deleteDialog.periodId = period.id;
            this.deleteDialog.label = `${this.formatDate(period.start_date)} a ${this.formatDate(period.end_date)}`;
            this.deleteDialog.active = true;
        },

        async confirmDelete() {
            try {
                this.deleteDialog.loading = true;
                await hotelUnavailabilityPeriodService.delete(this.hotelId, this.deleteDialog.periodId);
                this.deleteDialog.active = false;
                await this.loadPeriods();
                this.$simpleDialog('Período de indisponibilidade removido com sucesso.', 'success');
            } catch (e) {
                this.$simpleDialog('Erro ao remover o período de indisponibilidade.', 'error');
            } finally {
                this.deleteDialog.loading = false;
            }
        },

        formatDate(dateStr) {
            if (!dateStr) return '';
            const datePart = String(dateStr).substring(0, 10);
            const [year, month, day] = datePart.split('-');
            return `${day}/${month}/${year}`;
        },

        calculateTotalDays(startDateStr, endDateStr) {
            if (!startDateStr || !endDateStr) return '';
            const start = new Date(String(startDateStr).substring(0, 10));
            const end = new Date(String(endDateStr).substring(0, 10));
            const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
            return days === 1 ? '1 dia' : `${days} dias`;
        },

        periodStatus(item) {
            const today = new Date().toISOString().substring(0, 10);
            const start = String(item.start_date).substring(0, 10);
            const end   = String(item.end_date).substring(0, 10);
            if (end < today)   return { label: 'Expirado', color: 'grey lighten-1', dark: false };
            if (start > today) return { label: 'Futuro',   color: 'warning',        dark: false };
            return               { label: 'Ativo',     color: 'success',        dark: true  };
        },
    },
};
</script>

<style scoped>
.section-title {
    font-size: 1.35rem;
    color: rgba(0, 0, 0, 0.6);
}

.section-desc {
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 8px;
}

.entry-label {
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 16px;
    letter-spacing: 0.01em;
}
</style>
